import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Input, Pagination, Button, Icon } from 'semantic-ui-react';

// Data
import { useSurveyDataContext } from "../Data/SurveyViewContext";

// Components
import SubMenuTemplate from "new_components/templates/SubMenuTemplate";
import Text from "new_components/atoms/Text";
import SummaryChart from "./SummaryChart";
import SectionHeader from "./SectionHeader";
import EmptyContent from "new_components/templates/states/EmptyContent";
import ConversationModal from './ConversationModal';
import AverageDial from "./AverageDial";

// Constants
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import typography from "styles/typography";
import { useSelector } from "react-redux";

import { MiniILIcon } from "app/audit/simple/components/Icons";
import CustomIcon from "new_components/atoms/Icon";

const ResponsesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: -40px;
`;

const ResponseContainer = styled.div`
  margin-top: 10px;
  white-space: pre-wrap;
`;

const ResponseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 40px;
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid ${colors.neutral[200]};

  &:hover {
    background-color: ${colors.neutral[50]};
  }
`;

const Chip = styled.div`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 8px;
  background-color: #F1FAF7;
  font-size: 12px;
  margin-right: 5px;
  font-weight: 600;
  color: #27CDA7;
`;

const IconSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-top: 2px;
`;

const FollowupTextSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 4px;
`;

const UserWrapper = styled.div`
  display: flex;
  margin-bottom: 8px;
  cursor: ${(props) => (props.onClick ? "pointer" : "default")};

  &:hover {
    text-decoration: ${(props) => (props.onClick ? "underline" : "none")};
  }
`;

const FollowupSection = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  padding: 10px;
  background-color: ${colors.primary[50]};
  border-radius: 8px;
`;

const CaretButton = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: ${colors.neutral[600]};

  &:hover {
    background-color: ${colors.neutral[100]};
  }
`;

const Caret = styled(Icon)`
  &&& {
    margin: 0;
    font-size: 14px;
  }
`;

const removeFeedback = (question) => {
  return question?.replace(/\*\*Feedback:\*\*.*$/, "").trim();
};

const StyledInput = styled(Input)`
  &&& {
    width: 100%;
    margin-bottom: 10px;
    border-radius: 8px;
    background-color: #f6f9ff;
    padding: 5px;

    input {
      border: none;
      background: none;
      font-size: 12px;
      color: #6b7280;
      padding-left: 15px;
      &::placeholder {
        color: #b0b7c3;
      }
    }

    .icon {
      margin-right: 15px;
      color: #6b7280;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 85%;
  padding-right: 10px;
  margin-top: 20px;
`;

const ViewFullButton = styled(Button)`
  &&& {
    margin-top: 8px;
    background: transparent;
    color: ${colors.primary[500]};
    padding: 8px 16px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      background: ${colors.primary[50]};
    }
  }
`;

const HighlightedText = styled.span`
  background-color: ${colors.auxiliary.yellow[50]};
  padding: 0 2px;
  border-radius: 2px;
`;

const TruncatedText = styled.div`
  position: relative;
  max-height: 60px;
  overflow: hidden;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const getHighlightedText = (text, searchTerm) => {
  if (!searchTerm) return text;

  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === searchTerm.toLowerCase()
      ? <HighlightedText key={i}>{part}</HighlightedText>
      : part
  );
};

const getTruncatedText = (text, searchTerm, maxLength = 150) => {
  if (!searchTerm) {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  const searchIndex = text.toLowerCase().indexOf(searchTerm.toLowerCase());
  if (searchIndex === -1) return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

  // Calculate the start and end positions to show the search term with context
  const start = Math.max(0, searchIndex - 50);
  const end = Math.min(text.length, searchIndex + searchTerm.length + 50);

  let result = '';
  if (start > 0) result += '...';
  result += text.substring(start, end);
  if (end < text.length) result += '...';

  return result;
};

const QuestionTypeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const QuestionTypeText = styled(Text)`
  &&& {
    font-size: 12px;
    font-weight: 400;
    color: ${colors.text.gray};
  }
`;

const QuestionText = styled(Text)`
  display: flex;
  flex-direction: row;
  &&& {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.text.dark};
    margin-bottom: 12px;
  }
`;

const QuestionTypeIcon = styled(Icon)`
  &&& {
    color: ${colors.primary[500]};
    font-size: 20px;
    margin: 0;
  }
`;

const ScoreText = styled.div`
  // color: ${colors.primary[500]};
`;

const QuestionResponse = ({
  responses,
  handleUserSelect,
  questionData,
  surveyIsAnonymous,
}) => {
  const [search, setSearch] = useState("");
  const [filteredResponses, setFilteredResponses] = useState(responses);
  const [activePage, setActivePage] = useState(1);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const itemsPerPage = 3;

  useEffect(() => {
    const filtered = !questionData.type
      ? responses.filter(response => {
        const mainResponse = response?.task_list?.[0]?.response?.toLowerCase() || '';
        const followupResponse = response?.task_list?.[0]?.follow_up?.response?.toLowerCase() || '';
        const searchTerm = search.toLowerCase();
        return mainResponse.includes(searchTerm) || followupResponse.includes(searchTerm);
      })
      : responses;
    setFilteredResponses(filtered);
    setActivePage(1);
  }, [search, responses, questionData.type]);

  // Calculate paginated responses
  const totalPages = Math.ceil(filteredResponses.length / itemsPerPage);
  const startIndex = (activePage - 1) * itemsPerPage;
  const paginatedResponses = filteredResponses.slice(startIndex, startIndex + itemsPerPage);

  const handleViewFullConversation = (response) => {
    setSelectedConversation({
      question: questionData.question,
      mainResponse: response?.task_list?.[0]?.response,
      followUpQuestion: response?.task_list?.[0]?.follow_up?.question,
      followUp: response?.task_list?.[0]?.follow_up?.response
    });
  };

  if (questionData.type === "scale" || questionData.type === "multi" || questionData.type === "single") {
    // Create an array of follow-ups
    const followUps = responses.map((response) => {
      return response?.task_list?.[0]?.follow_up;
    });

    // Calculate average score if it's a scale question
    const calculateAverage = () => {
      const validResponses = responses.filter(r =>
        r?.task_list?.[0]?.response !== undefined &&
        !isNaN(parseInt(r?.task_list?.[0]?.response))
      );

      if (validResponses.length === 0) return null;

      const sum = validResponses.reduce((acc, r) =>
        acc + parseInt(r?.task_list?.[0]?.response), 0
      );

      return (sum / validResponses.length).toFixed(1);
    };

    // Get scale max value from questionData.scale (e.g. "0-5" -> 5, "1-10" -> 10)
    const getScaleMax = () => {
      if (!questionData.scale) return null;

      const scaleParts = questionData.scale.split('-');
      if (scaleParts.length !== 2) return null;

      return parseInt(scaleParts[1]);
    };

    const averageScore = calculateAverage();
    const scaleMax = getScaleMax();

    return (
      <ResponsesWrapper>
        <QuestionTypeHeader>
          <CustomIcon
            name={
              questionData.type === 'scale' ? ICON_SET.pulseScale :
                questionData.type === 'single' ? ICON_SET.pulseSingle :
                  questionData.type === 'multi' ? ICON_SET.pulseMulti :
                    ICON_SET.pulseText
            }
            style={{ fontSize: '16px', color: colors.text.gray }}
          />
          <QuestionTypeText>
            {questionData.type === 'scale' ? 'Scale Question' :
              questionData.type === 'single' ? 'Multiple Choice Question' :
                questionData.type === 'multi' ? 'Multiple Select Question' :
                  "Open Text"}
          </QuestionTypeText>
        </QuestionTypeHeader>
        <QuestionText>
          {questionData.question}&nbsp;&nbsp;<ScoreText>(Overall {parseFloat(averageScore) * 10}%)</ScoreText>
        </QuestionText>
        {/* Display the AverageDial component instead of the badge */}
        {/* {averageScore && scaleMax && (
          <AverageDial
            averageScore={parseFloat(averageScore)}
            scaleMax={scaleMax}
          />
        )} */}

        {/* RENDER THE CHART IF IT IS A SCALE QUESTION */}
        {(questionData.type === "scale" || questionData.type === "multi" || questionData.type === "single") && (
          <div style={{ margin: "20px 0" }}>
            <SummaryChart questionData={questionData} responses={responses} />
          </div>
        )}
        {followUps?.filter((f) => f?.question)?.filter((f) => f?.response)
          ?.length > 0 && (
          <Text typographyStyle={typography.subhead.semibold}>Responses</Text>
        )}
        {followUps?.length > 0 &&
          followUps
            ?.filter((f) => f?.question)
            ?.filter((f) => f?.response)
            .map((fu) => (
              <FollowupSection>
                <IconSection>
                  {MiniILIcon()}
                </IconSection>
                <FollowupTextSection>

                <Text
                  typographyStyle={typography.caption.semibold}
                  color={colors.primary[500]}
                >
                  AI Follow up
                </Text>
                <Text typographyStyle={typography.caption.semibold}>
                  {removeFeedback(fu?.question)}
                </Text>
                <ResponseContainer>
                  <Text typographyStyle={typography.caption.regular}>
                    {fu?.response}
                  </Text>
                  </ResponseContainer>
                </FollowupTextSection>
              </FollowupSection>
            ))}
      </ResponsesWrapper>
    );
  }

  return (
    <ResponsesWrapper>
      {!questionData.type || questionData.type?.value === "text" && (
        <StyledInput
          icon={{ name: "search" }}
          placeholder="Search Keywords..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <Text typographyStyle={typography.subhead.semibold}>Responses</Text>
      {paginatedResponses.map((response, index) => (
        <ResponseWrapper
          key={index}
          onClick={() => handleViewFullConversation(response)}
        >
          {!surveyIsAnonymous ? (
            <UserWrapper onClick={(e) => {
              e.stopPropagation();
              handleUserSelect(response);
            }}>
              <Chip>
                {response.participant_name}
              </Chip>
            </UserWrapper>
          ) : (
            <UserWrapper>
              <Chip>
                {response.participant_name}
              </Chip>
            </UserWrapper>
          )}
          <TruncatedText>
            <Text typographyStyle={typography.caption.regular}>
              {getHighlightedText(
                getTruncatedText(response?.task_list?.[0]?.response, search),
                search
              )}
            </Text>
          </TruncatedText>
          {response?.task_list?.[0]?.follow_up &&
            response?.task_list?.[0]?.follow_up?.response?.length > 0 && (
            <FollowupSection key={index}>
              <IconSection>
                {MiniILIcon()}
              </IconSection>
              <FollowupTextSection>
                <Text
                  typographyStyle={typography.caption.semibold}
                  color={colors.primary[500]}
                >
                  AI Follow up
                </Text>
                <Text typographyStyle={typography.caption.semibold}>
                  {removeFeedback(
                    response?.task_list?.[0]?.follow_up?.question
                  )}
                </Text>
                <ResponseContainer>
                  <TruncatedText>
                    <Text typographyStyle={typography.caption.regular}>
                      {getHighlightedText(
                        getTruncatedText(response?.task_list?.[0]?.follow_up?.response, search),
                        search
                      )}
                    </Text>
                  </TruncatedText>
                </ResponseContainer>
              </FollowupTextSection>
            </FollowupSection>
            )}
          <CaretButton>
            <Caret name="chevron right" />
          </CaretButton>
        </ResponseWrapper>
      ))}
      {(!questionData.type || questionData.type?.value === "text") && filteredResponses.length > itemsPerPage && (
        <PaginationWrapper>
          <Pagination
            activePage={activePage}
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
            totalPages={totalPages}
            onPageChange={(e, { activePage }) => setActivePage(activePage)}
          />
        </PaginationWrapper>
      )}
      <ConversationModal
        open={!!selectedConversation}
        onClose={() => setSelectedConversation(null)}
        conversation={selectedConversation}
      />
    </ResponsesWrapper>
  );
};

const QuestionsView = ({
  questions,
  responses,
  handleUserSelect,
  participants,
  filters,
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const responseData = responses.map((rGroup) => rGroup?.task_response);

  const { surveyIsAnonymous } = useSurveyDataContext();
  const coreData = useSelector(state => state.audit.core_data);
  const defaultRule = coreData?.Whitelabel?.rule || 3;

  const menuItems = questions.map((question, index) => {
    // within each responseData object, loop through task_list and
    // remove every task_list that does not match the current index
    // then return the task_response object
    const response = responseData.map((rGroup) => {
      return {
        ...rGroup,
        participant_name:
          surveyIsAnonymous === true
            ? "Anonymous Participant"
            : participants.find(
                (participant) => participant.id === rGroup.employee_id
              )?.employee_full_name,
        task_list: rGroup.task_list.filter((task, i) => i === index),
      };
    });

    let content = null;


    if (surveyIsAnonymous && response?.length < defaultRule) {
      content = <EmptyContent pageTitle="No Responses Visible" emptyText="There are not enough responses yet to show responses to this survey." />;
    } else {
      content = (
        <QuestionResponse
          question={question.question}
          responses={response}
          handleUserSelect={handleUserSelect}
          questionData={question}
          surveyIsAnonymous={surveyIsAnonymous}
        />
      );
    }

    return {
      id: question.id,
      categoryTitle: question.question,
      categoryContent: content,
    };
  });

  return (
    <SubMenuTemplate
      onItemSelect={setSelectedQuestion}
      menuItems={menuItems}
      isPulse={true}
      type="single"
      actionRow={<SectionHeader text="Questions" icon={ICON_SET.question} filters={filters} />}
    />
  );
};

export default QuestionsView;
